import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { api, ApiError } from './api';

const fmtPeso = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(n || 0);

const escaparHtml = (valor = '') => String(valor)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const urlAbsoluta = (url) => {
  if (!url) return '';
  try { return new URL(url, window.location.origin).href; }
  catch { return String(url); }
};

const encabezadoBranding = (branding, titulo) => {
  const nombre = escaparHtml(branding?.nombre_negocio || 'Casa Alessio');
  const logo = branding?.logo_url
    ? `<img class="logo-reporte" src="${escaparHtml(urlAbsoluta(branding.logo_url))}" alt="${nombre}" />`
    : '';
  return `<header class="encabezado-reporte">${logo}<div class="encabezado-texto"><div class="nombre-negocio">${nombre}</div><div class="titulo-reporte">${escaparHtml(titulo)}</div></div></header>`;
};

export default function Reportes({ branding = {} }) {
  const [pestaña, setPestaña] = useState('ventas');
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);

  const [datosVentas, setDatosVentas] = useState(null);
  const [cierresCaja, setCierresCaja] = useState([]);

  // Libro IVA y Rentabilidad
  const [libroIvaVentas, setLibroIvaVentas] = useState(null);
  const [libroIvaCompras, setLibroIvaCompras] = useState(null);
  const [rentabilidad, setRentabilidad] = useState(null);
  const [subPestañaIva, setSubPestañaIva] = useState('ventas');
  const [cargandoIva, setCargandoIva] = useState(false);

  const generarReporteVentas = async () => {
    try {
      const data = await api.get(`/api/reportes/ventas?inicio=${fechaInicio}&fin=${fechaFin}`);
      setDatosVentas(data);
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error al generar el reporte.'));
    }
  };

  const cargarCierresCaja = async () => {
    try {
      const data = await api.get('/api/reportes/cajas');
      setCierresCaja(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error al cargar cierres:", err);
    }
  };

  const generarLibroIva = async () => {
    setCargandoIva(true);
    setLibroIvaVentas(null);
    setLibroIvaCompras(null);
    setRentabilidad(null);
    try {
      const [v, c, r] = await Promise.all([
        api.get(`/api/libro-iva/ventas?desde=${fechaInicio}&hasta=${fechaFin}`),
        api.get(`/api/libro-iva/compras?desde=${fechaInicio}&hasta=${fechaFin}`),
        api.get(`/api/libro-iva/rentabilidad?desde=${fechaInicio}&hasta=${fechaFin}`),
      ]);
      setLibroIvaVentas(v);
      setLibroIvaCompras(c);
      setRentabilidad(r);
    } catch (err) {
      alert("❌ " + (err instanceof ApiError ? err.mensaje : 'Error al generar el Libro IVA.'));
    } finally {
      setCargandoIva(false);
    }
  };

  useEffect(() => { if (pestaña === 'cajas') cargarCierresCaja(); }, [pestaña]);

  const exportarLibroIvaExcel = () => {
    if (!libroIvaVentas || !libroIvaCompras) return alert('Generá el Libro IVA primero.');
    const wb = XLSX.utils.book_new();

    const wsV = XLSX.utils.json_to_sheet(libroIvaVentas.filas.map(f => ({
      'Fecha': f.fecha,
      'Tipo': f.tipo_comprobante,
      'N° Comprobante': f.numero_comprobante,
      'CAE': f.cae || '',
      'Cliente': f.cliente_nombre,
      'CUIT Cliente': f.cliente_cuit || '',
      'Neto Gravado': f.neto_gravado,
      'IVA (21%)': f.iva,
      'Total': f.total,
    })));
    XLSX.utils.book_append_sheet(wb, wsV, 'IVA Ventas');

    const wsC = XLSX.utils.json_to_sheet(libroIvaCompras.filas.map(f => ({
      'Fecha': f.fecha,
      'Tipo': f.tipo_comprobante,
      'N° Comprobante': f.numero_comprobante,
      'Proveedor': f.proveedor_nombre,
      'CUIT Proveedor': f.proveedor_cuit || '',
      'Neto Gravado': f.neto_gravado,
      'IVA Crédito Fiscal': f.iva_credito_fiscal,
      'Otros Tributos': f.otros_tributos,
      'Total': f.total,
    })));
    XLSX.utils.book_append_sheet(wb, wsC, 'IVA Compras');

    if (rentabilidad) {
      const wsR = XLSX.utils.json_to_sheet(rentabilidad.filas.map(f => ({
        'Categoría': f.categoria,
        'Ventas': f.ventas,
        'Costo Mercadería': f.costo_mercaderia,
        'Ganancia Bruta': f.ganancia_bruta,
        'Margen %': f.margen_porcentaje,
      })));
      XLSX.utils.book_append_sheet(wb, wsR, 'Rentabilidad');
    }

    XLSX.writeFile(wb, `LibroIVA_${fechaInicio}_${fechaFin}.xlsx`);
  };

  const imprimirPDF = () => {
    let contenidoHTML = '';
    let titulo = 'Reporte';

    if (pestaña === 'ventas') {
      if (!datosVentas) {
        alert('Generá el reporte de ventas primero.');
        return;
      }
      titulo = 'Reporte de Ventas por Rubro';
      contenidoHTML = `
        <h2>${titulo}</h2>
        <p class="periodo">Desde: ${fechaInicio} | Hasta: ${fechaFin}</p>
        <h3>Ventas por Categoría</h3>
        <table><thead><tr><th>Categoría</th><th class="num">Facturado</th></tr></thead><tbody>
        ${(datosVentas.categorias || []).map(c => `<tr><td>${c.categoria}</td><td class="num strong">${fmtPeso(c.total)}</td></tr>`).join('')}
        </tbody></table>
        <h3>Métodos de Pago</h3>
        <table><thead><tr><th>Método</th><th class="num">Total</th></tr></thead><tbody>
        ${(datosVentas.metodos || []).map(m => `<tr><td>${m.metodo_pago}</td><td class="num strong">${fmtPeso(m.total)}</td></tr>`).join('')}
        </tbody></table>
      `;
    } else if (pestaña === 'cajas') {
      if (!cierresCaja.length) {
        alert('No hay cierres de caja para imprimir.');
        return;
      }
      titulo = 'Reporte de Cierres de Caja';
      contenidoHTML = `
        <h2>${titulo}</h2>
        <p class="periodo">Generado: ${new Date().toLocaleString('es-AR')}</p>
        <table>
          <thead><tr><th>Apertura</th><th>Cierre</th><th>Cajero</th><th class="num">Esperado</th><th class="num">Contado</th><th class="num">Diferencia</th></tr></thead>
          <tbody>${cierresCaja.map(c => {
            const esperado = Number.parseFloat(c.monto_esperado || 0);
            const contado = Number.parseFloat(c.monto_final || c.efectivo_contado || 0);
            return `<tr>
              <td>${new Date(c.fecha_apertura).toLocaleString('es-AR')}</td>
              <td>${c.fecha_cierre ? new Date(c.fecha_cierre).toLocaleString('es-AR') : '-'}</td>
              <td>${c.cajero || '-'}</td>
              <td class="num">${fmtPeso(esperado)}</td>
              <td class="num">${fmtPeso(contado)}</td>
              <td class="num strong">${fmtPeso(contado - esperado)}</td>
            </tr>`;
          }).join('')}</tbody>
        </table>
      `;
    } else if (pestaña === 'iva') {
      if (!libroIvaVentas) {
        alert('Generá el Libro IVA primero.');
        return;
      }

      if (subPestañaIva === 'ventas') {
        titulo = 'Libro IVA Ventas';
        contenidoHTML = `
          <h2>${titulo}</h2><p class="periodo">Desde: ${fechaInicio} | Hasta: ${fechaFin}</p>
          <table><thead><tr><th>Fecha</th><th>Tipo</th><th>Número</th><th>Cliente</th><th>CUIT</th><th class="num">Neto</th><th class="num">IVA</th><th class="num">Total</th></tr></thead>
          <tbody>${libroIvaVentas.filas.map(f => `<tr><td>${f.fecha}</td><td>${f.tipo_comprobante}</td><td>${f.numero_comprobante}</td><td>${f.cliente_nombre}</td><td>${f.cliente_cuit || '-'}</td><td class="num">${fmtPeso(f.neto_gravado)}</td><td class="num">${fmtPeso(f.iva)}</td><td class="num strong">${fmtPeso(f.total)}</td></tr>`).join('')}
          <tr class="totales"><td colspan="5">TOTALES</td><td class="num">${fmtPeso(libroIvaVentas.totales.neto_gravado)}</td><td class="num">${fmtPeso(libroIvaVentas.totales.iva)}</td><td class="num">${fmtPeso(libroIvaVentas.totales.total)}</td></tr></tbody></table>`;
      } else if (subPestañaIva === 'compras') {
        if (!libroIvaCompras) return alert('No hay datos de IVA Compras.');
        titulo = 'Libro IVA Compras';
        contenidoHTML = `
          <h2>${titulo}</h2><p class="periodo">Desde: ${fechaInicio} | Hasta: ${fechaFin}</p>
          <table><thead><tr><th>Fecha</th><th>Tipo</th><th>Número</th><th>Proveedor</th><th>CUIT</th><th class="num">Neto</th><th class="num">IVA</th><th class="num">Total</th></tr></thead>
          <tbody>${libroIvaCompras.filas.map(f => `<tr><td>${f.fecha}</td><td>${f.tipo_comprobante}</td><td>${f.numero_comprobante}</td><td>${f.proveedor_nombre}</td><td>${f.proveedor_cuit || '-'}</td><td class="num">${fmtPeso(f.neto_gravado)}</td><td class="num">${fmtPeso(f.iva_credito_fiscal)}</td><td class="num strong">${fmtPeso(f.total)}</td></tr>`).join('')}
          <tr class="totales"><td colspan="5">TOTALES</td><td class="num">${fmtPeso(libroIvaCompras.totales.neto_gravado)}</td><td class="num">${fmtPeso(libroIvaCompras.totales.iva_credito_fiscal)}</td><td class="num">${fmtPeso(libroIvaCompras.totales.total)}</td></tr></tbody></table>`;
      } else {
        if (!rentabilidad) return alert('No hay datos de rentabilidad.');
        titulo = 'Reporte de Rentabilidad';
        contenidoHTML = `
          <h2>${titulo}</h2><p class="periodo">Desde: ${fechaInicio} | Hasta: ${fechaFin}</p>
          <table><thead><tr><th>Categoría</th><th class="num">Ventas</th><th class="num">Costo</th><th class="num">Ganancia Bruta</th><th class="num">Margen</th></tr></thead>
          <tbody>${rentabilidad.filas.map(f => `<tr><td>${f.categoria}</td><td class="num">${fmtPeso(f.ventas)}</td><td class="num">${fmtPeso(f.costo_mercaderia)}</td><td class="num strong">${fmtPeso(f.ganancia_bruta)}</td><td class="num">${Number.parseFloat(f.margen_porcentaje || 0).toFixed(2)}%</td></tr>`).join('')}</tbody></table>`;
      }
    }

    const ventana = window.open('', '_blank', 'width=1000,height=800');
    if (!ventana) {
      alert('El navegador bloqueó la ventana del reporte. Permití ventanas emergentes para este sitio.');
      return;
    }

    ventana.document.open();
    const cabecera = encabezadoBranding(branding, titulo);
    ventana.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${titulo}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#0f172a}.encabezado-reporte{display:flex;align-items:center;justify-content:center;gap:18px;border-bottom:2px solid #0f172a;padding:0 0 14px;margin:0 0 18px;min-height:70px}.logo-reporte{display:block;max-width:150px;max-height:68px;object-fit:contain}.encabezado-texto{text-align:left}.nombre-negocio{font-size:20px;font-weight:800;line-height:1.1}.titulo-reporte{font-size:13px;color:#475569;margin-top:5px;font-weight:700}h2{text-align:center;margin-bottom:4px}.periodo{text-align:center;color:#64748b;margin-top:0;margin-bottom:24px}
        h3{margin-top:24px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#e2e8f0;padding:7px;text-align:left;border:1px solid #cbd5e1}td{padding:7px;border:1px solid #e2e8f0}.num{text-align:right}.strong{font-weight:bold}.totales{font-weight:bold;background:#f1f5f9}
        @media print{@page{size:A4 landscape;margin:10mm}body{padding:0}.encabezado-reporte{break-inside:avoid}}
      </style></head><body>${cabecera}${contenidoHTML}</body></html>`);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => ventana.print(), 700);
  };

  const exportarAExcel = () => {
    if (pestaña === 'iva') { exportarLibroIvaExcel(); return; }
    let data = [];
    let nombreArchivo = `Reporte_${pestaña}.xlsx`;
    if (pestaña === 'ventas' && datosVentas) {
      data = (datosVentas.categorias || []).map(c => ({ 'Categoría': c.categoria, 'Monto': parseFloat(c.total) }));
    } else if (pestaña === 'cajas') {
      data = cierresCaja.map(c => ({
        'Apertura': new Date(c.fecha_apertura).toLocaleString(),
        'Cierre': c.fecha_cierre ? new Date(c.fecha_cierre).toLocaleString() : '-',
        'Cajero': c.cajero || 'Admin',
        'Esperado': parseFloat(c.monto_esperado),
        'Físico': parseFloat(c.monto_final),
        'Diferencia': parseFloat(c.monto_final) - parseFloat(c.monto_esperado),
      }));
    }
    if (data.length === 0) return alert("No hay datos para exportar");
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reporte");
    XLSX.writeFile(wb, nombreArchivo);
  };

  const tabBtn = (key, label, color) => (
    <button onClick={() => setPestaña(key)}
      style={{ flex: 1, padding: '13px 8px', fontWeight: 'bold', borderRadius: '5px', border: 'none', cursor: 'pointer', backgroundColor: pestaña === key ? color : '#f1f5f9', color: pestaña === key ? 'white' : '#64748b', fontSize: '13px' }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', minHeight: '80vh' }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #38bdf8', paddingBottom: '10px', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ color: '#0f172a', margin: 0 }}>📊 Panel de Reportes</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={exportarAExcel} style={{ backgroundColor: '#10b981', color: 'white', padding: '10px 15px', borderRadius: '6px', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>⬇️ Bajar Excel</button>
          <button onClick={imprimirPDF} style={{ backgroundColor: '#0f172a', color: 'white', padding: '10px 15px', borderRadius: '6px', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>🖨️ Imprimir PDF</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
        {tabBtn('ventas', '📈 Ventas por Rubro', '#0ea5e9')}
        {tabBtn('cajas', '🔒 Cierres de Caja', '#f59e0b')}
        {tabBtn('iva', '🧾 Libro IVA y Rentabilidad', '#8b5cf6')}
      </div>

      {/* Filtro de fechas (compartido entre pestañas) */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '20px', backgroundColor: '#f8fafc', padding: '12px 16px', borderRadius: '8px', flexWrap: 'wrap' }}>
        <div><label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px' }}>Desde:</label><input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} style={{ padding: '8px 10px', borderRadius: '5px', border: '1px solid #cbd5e1' }} /></div>
        <div><label style={{ display: 'block', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px' }}>Hasta:</label><input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} style={{ padding: '8px 10px', borderRadius: '5px', border: '1px solid #cbd5e1' }} /></div>
        {pestaña === 'ventas' && <button onClick={generarReporteVentas} style={{ padding: '8px 18px', backgroundColor: '#0ea5e9', color: 'white', fontWeight: 'bold', borderRadius: '5px', border: 'none', cursor: 'pointer' }}>🔍 Generar</button>}
        {pestaña === 'iva' && <button onClick={generarLibroIva} disabled={cargandoIva} style={{ padding: '8px 18px', backgroundColor: cargandoIva ? '#a78bfa' : '#8b5cf6', color: 'white', fontWeight: 'bold', borderRadius: '5px', border: 'none', cursor: 'pointer' }}>
          {cargandoIva ? '⏳ Generando...' : '🔍 Generar Libro IVA'}
        </button>}
      </div>

      {/* ======================== VENTAS POR RUBRO ======================== */}
      {pestaña === 'ventas' && datosVentas && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
          <div>
            <h3 style={{ color: '#0369a1' }}>📦 Ventas por Categoría</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0' }}>
              <thead style={{ backgroundColor: '#f1f5f9' }}><tr><th style={{ padding: '10px' }}>Categoría</th><th style={{ padding: '10px', textAlign: 'right' }}>Facturado</th></tr></thead>
              <tbody>
                {(datosVentas.categorias || []).map((c, i) => (
                  <tr key={i}><td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0', fontWeight: 'bold' }}>{c.categoria}</td><td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', color: '#10b981', fontWeight: 'bold' }}>{fmtPeso(c.total)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <h3 style={{ color: '#be123c' }}>💳 Método de Pago</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e2e8f0' }}>
              <thead style={{ backgroundColor: '#f1f5f9' }}><tr><th style={{ padding: '10px' }}>Método</th><th style={{ padding: '10px', textAlign: 'right' }}>Total</th></tr></thead>
              <tbody>
                {(datosVentas.metodos || []).map((m, i) => (
                  <tr key={i}><td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0', fontWeight: 'bold' }}>{m.metodo_pago}</td><td style={{ padding: '10px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', color: '#2563eb', fontWeight: 'bold' }}>{fmtPeso(m.total)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ======================== CIERRES DE CAJA ======================== */}
      {pestaña === 'cajas' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead style={{ backgroundColor: '#fef3c7' }}>
              <tr>
                {['Apertura - Cierre', 'Cajero', 'Sistema (Esperado)', 'Billetes (Físico)', 'Diferencia'].map((h, i) =>
                  <th key={i} style={{ padding: '12px', textAlign: i > 1 ? 'right' : 'left', borderBottom: '2px solid #fde68a' }}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {cierresCaja.map(c => {
                const esperado = parseFloat(c.monto_esperado) || 0;
                const real = parseFloat(c.monto_final) || 0;
                const dif = real - esperado;
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px', fontSize: '13px', color: '#475569' }}>
                      {new Date(c.fecha_apertura).toLocaleString('es-AR')}<br/>
                      <strong>{c.fecha_cierre ? new Date(c.fecha_cierre).toLocaleString('es-AR') : 'Sin cierre'}</strong>
                    </td>
                    <td style={{ padding: '12px', fontWeight: 'bold' }}>{c.cajero || 'Admin'}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#3b82f6', fontWeight: 'bold' }}>{fmtPeso(esperado)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#10b981', fontWeight: 'bold' }}>{fmtPeso(real)}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: dif < 0 ? '#ef4444' : dif > 0 ? '#10b981' : '#64748b' }}>
                      {dif > 0 ? '+' : ''}{fmtPeso(dif)}
                    </td>
                  </tr>
                );
              })}
              {cierresCaja.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>No hay cierres históricos.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ======================== LIBRO IVA ======================== */}
      {pestaña === 'iva' && (
        <div>
          {!libroIvaVentas && !cargandoIva && (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
              <p style={{ fontSize: '40px', margin: '0 0 10px 0' }}>🧾</p>
              <p>Seleccioná el período y hacé clic en <strong>Generar Libro IVA</strong></p>
            </div>
          )}

          {libroIvaVentas && (
            <>
              {/* Sub-pestañas IVA */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                {[['ventas', '📤 IVA Ventas'], ['compras', '📥 IVA Compras'], ['rentabilidad', '📊 Rentabilidad']].map(([k, l]) => (
                  <button key={k} onClick={() => setSubPestañaIva(k)}
                    style={{ padding: '8px 18px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px',
                      backgroundColor: subPestañaIva === k ? '#8b5cf6' : '#f1f5f9', color: subPestañaIva === k ? 'white' : '#64748b' }}>
                    {l}
                  </button>
                ))}
              </div>

              {/* IVA VENTAS */}
              {subPestañaIva === 'ventas' && libroIvaVentas && (
                <div>
                  {/* Totalizadores */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    {[
                      { label: 'Neto Gravado', val: fmtPeso(libroIvaVentas.totales.neto_gravado), color: '#0ea5e9' },
                      { label: 'IVA Débito Fiscal', val: fmtPeso(libroIvaVentas.totales.iva), color: '#f59e0b' },
                      { label: 'Total Ventas', val: fmtPeso(libroIvaVentas.totales.total), color: '#10b981' },
                      { label: 'Facturas A', val: libroIvaVentas.totales.cant_facturas_a, color: '#8b5cf6' },
                      { label: 'Facturas B', val: libroIvaVentas.totales.cant_facturas_b, color: '#64748b' },
                      { label: 'Tickets', val: libroIvaVentas.totales.cant_tickets, color: '#64748b' },
                    ].map((k, i) => (
                      <div key={i} style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', borderLeft: `4px solid ${k.color}` }}>
                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>{k.label}</p>
                        <p style={{ margin: '4px 0 0 0', fontWeight: 'bold', fontSize: '16px', color: k.color }}>{k.val}</p>
                      </div>
                    ))}
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px', fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#f1f5f9' }}>
                        <tr>{['Fecha', 'Tipo', 'N° Comprobante', 'CAE', 'Cliente', 'CUIT', 'Neto Grav.', 'IVA', 'Total', 'Medio Pago'].map((h, i) =>
                          <th key={i} style={{ padding: '9px 10px', textAlign: i > 5 ? 'right' : 'left', borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>{h}</th>
                        )}</tr>
                      </thead>
                      <tbody>
                        {libroIvaVentas.filas.map((f, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 10px' }}>{new Date(f.fecha).toLocaleDateString('es-AR')}</td>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ backgroundColor: f.tipo_comprobante === 'A' ? '#fef3c7' : '#f1f5f9', color: f.tipo_comprobante === 'A' ? '#92400e' : '#64748b', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', fontSize: '11px' }}>
                                {f.tipo_comprobante}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', color: '#64748b' }}>{f.numero_comprobante}</td>
                            <td style={{ padding: '8px 10px', color: '#94a3b8', fontSize: '11px' }}>{f.cae || '—'}</td>
                            <td style={{ padding: '8px 10px', fontWeight: '500' }}>{f.cliente_nombre}</td>
                            <td style={{ padding: '8px 10px', color: '#64748b' }}>{f.cliente_cuit || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtPeso(f.neto_gravado)}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', color: f.iva > 0 ? '#f59e0b' : '#94a3b8' }}>{fmtPeso(f.iva)}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold' }}>{fmtPeso(f.total)}</td>
                            <td style={{ padding: '8px 10px', color: '#64748b', fontSize: '12px' }}>{f.forma_pago}</td>
                          </tr>
                        ))}
                        <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>
                          <td colSpan="6" style={{ padding: '10px', textAlign: 'right' }}>TOTALES:</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{fmtPeso(libroIvaVentas.totales.neto_gravado)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#f59e0b' }}>{fmtPeso(libroIvaVentas.totales.iva)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#10b981' }}>{fmtPeso(libroIvaVentas.totales.total)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* IVA COMPRAS */}
              {subPestañaIva === 'compras' && libroIvaCompras && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    {[
                      { label: 'Neto Gravado', val: fmtPeso(libroIvaCompras.totales.neto_gravado), color: '#0ea5e9' },
                      { label: 'IVA Crédito Fiscal', val: fmtPeso(libroIvaCompras.totales.iva_credito_fiscal), color: '#8b5cf6' },
                      { label: 'Otros Tributos', val: fmtPeso(libroIvaCompras.totales.otros_tributos), color: '#64748b' },
                      { label: 'Total Compras', val: fmtPeso(libroIvaCompras.totales.total), color: '#ef4444' },
                    ].map((k, i) => (
                      <div key={i} style={{ backgroundColor: '#f8fafc', padding: '12px', borderRadius: '8px', borderLeft: `4px solid ${k.color}` }}>
                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>{k.label}</p>
                        <p style={{ margin: '4px 0 0 0', fontWeight: 'bold', fontSize: '16px', color: k.color }}>{k.val}</p>
                      </div>
                    ))}
                  </div>

                  {/* Posición de IVA */}
                  {libroIvaVentas && (
                    <div style={{ backgroundColor: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#7c3aed', fontSize: '14px' }}>📌 Posición de IVA del período</h4>
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>IVA Débito (Ventas)</p><p style={{ margin: '2px 0 0 0', fontWeight: 'bold', color: '#f59e0b', fontSize: '18px' }}>{fmtPeso(libroIvaVentas.totales.iva)}</p></div>
                        <div style={{ fontSize: '24px', color: '#94a3b8', alignSelf: 'center' }}>−</div>
                        <div><p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>IVA Crédito (Compras)</p><p style={{ margin: '2px 0 0 0', fontWeight: 'bold', color: '#8b5cf6', fontSize: '18px' }}>{fmtPeso(libroIvaCompras.totales.iva_credito_fiscal)}</p></div>
                        <div style={{ fontSize: '24px', color: '#94a3b8', alignSelf: 'center' }}>=</div>
                        <div>
                          <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>Posición neta</p>
                          <p style={{ margin: '2px 0 0 0', fontWeight: 'bold', fontSize: '20px', color: (libroIvaVentas.totales.iva - libroIvaCompras.totales.iva_credito_fiscal) >= 0 ? '#dc2626' : '#16a34a' }}>
                            {fmtPeso(libroIvaVentas.totales.iva - libroIvaCompras.totales.iva_credito_fiscal)}
                          </p>
                          <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                            {(libroIvaVentas.totales.iva - libroIvaCompras.totales.iva_credito_fiscal) >= 0 ? 'A pagar a AFIP' : 'A favor del contribuyente'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px', fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#f1f5f9' }}>
                        <tr>{['Fecha', 'Tipo', 'N° Comprobante', 'Proveedor', 'CUIT', 'Neto Grav.', 'IVA CF', 'Otros', 'Total', 'Estado'].map((h, i) =>
                          <th key={i} style={{ padding: '9px 10px', textAlign: i > 4 ? 'right' : 'left', borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>{h}</th>
                        )}</tr>
                      </thead>
                      <tbody>
                        {libroIvaCompras.filas.map((f, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 10px' }}>{new Date(f.fecha).toLocaleDateString('es-AR')}</td>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ backgroundColor: f.tipo_comprobante === 'A' ? '#fef3c7' : '#f1f5f9', color: f.tipo_comprobante === 'A' ? '#92400e' : '#64748b', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold', fontSize: '11px' }}>
                                FCO {f.tipo_comprobante}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', color: '#64748b' }}>{f.numero_comprobante || '—'}</td>
                            <td style={{ padding: '8px 10px', fontWeight: '500' }}>{f.proveedor_nombre}</td>
                            <td style={{ padding: '8px 10px', color: '#64748b' }}>{f.proveedor_cuit || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right' }}>{fmtPeso(f.neto_gravado)}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', color: f.iva_credito_fiscal > 0 ? '#8b5cf6' : '#94a3b8' }}>{fmtPeso(f.iva_credito_fiscal)}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', color: '#64748b' }}>{fmtPeso(f.otros_tributos)}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold' }}>{fmtPeso(f.total)}</td>
                            <td style={{ padding: '8px 10px', fontSize: '11px', color: f.estado === 'Pagada' ? '#16a34a' : f.estado === 'Pendiente' ? '#dc2626' : '#d97706' }}>{f.estado}</td>
                          </tr>
                        ))}
                        <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>
                          <td colSpan="5" style={{ padding: '10px', textAlign: 'right' }}>TOTALES:</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{fmtPeso(libroIvaCompras.totales.neto_gravado)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#8b5cf6' }}>{fmtPeso(libroIvaCompras.totales.iva_credito_fiscal)}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{fmtPeso(libroIvaCompras.totales.otros_tributos)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', color: '#ef4444' }}>{fmtPeso(libroIvaCompras.totales.total)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* RENTABILIDAD */}
              {subPestañaIva === 'rentabilidad' && rentabilidad && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                    {[
                      { label: 'Total Ventas', val: fmtPeso(rentabilidad.totales.ventas), color: '#10b981' },
                      { label: 'Costo Mercadería', val: fmtPeso(rentabilidad.totales.costo_mercaderia), color: '#ef4444' },
                      { label: 'Ganancia Bruta', val: fmtPeso(rentabilidad.totales.ganancia_bruta), color: '#0ea5e9' },
                      { label: 'Margen Promedio', val: `${rentabilidad.totales.margen_porcentaje}%`, color: '#8b5cf6' },
                    ].map((k, i) => (
                      <div key={i} style={{ backgroundColor: '#f8fafc', padding: '14px', borderRadius: '8px', borderLeft: `4px solid ${k.color}` }}>
                        <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>{k.label}</p>
                        <p style={{ margin: '4px 0 0 0', fontWeight: 'bold', fontSize: '18px', color: k.color }}>{k.val}</p>
                      </div>
                    ))}
                  </div>

                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead style={{ backgroundColor: '#f1f5f9' }}>
                      <tr>{['Categoría', 'Ventas', 'Costo Mercadería', 'Ganancia Bruta', 'Margen %'].map((h, i) =>
                        <th key={i} style={{ padding: '10px 12px', textAlign: i > 0 ? 'right' : 'left', borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>{h}</th>
                      )}</tr>
                    </thead>
                    <tbody>
                      {rentabilidad.filas.map((f, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '10px 12px', fontWeight: '600' }}>{f.categoria}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#10b981', fontWeight: 'bold' }}>{fmtPeso(f.ventas)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444' }}>{fmtPeso(f.costo_mercaderia)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0ea5e9', fontWeight: 'bold' }}>{fmtPeso(f.ganancia_bruta)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            <span style={{ backgroundColor: f.margen_porcentaje >= 30 ? '#f0fdf4' : f.margen_porcentaje >= 15 ? '#fefce8' : '#fef2f2', color: f.margen_porcentaje >= 30 ? '#16a34a' : f.margen_porcentaje >= 15 ? '#ca8a04' : '#dc2626', padding: '3px 8px', borderRadius: '10px', fontWeight: 'bold' }}>
                              {f.margen_porcentaje}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: '#f1f5f9', fontWeight: 'bold' }}>
                        <td style={{ padding: '10px 12px' }}>TOTALES</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#10b981' }}>{fmtPeso(rentabilidad.totales.ventas)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#ef4444' }}>{fmtPeso(rentabilidad.totales.costo_mercaderia)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#0ea5e9' }}>{fmtPeso(rentabilidad.totales.ganancia_bruta)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#8b5cf6', fontWeight: 'bold' }}>{rentabilidad.totales.margen_porcentaje}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
